import {
  $applyNodeReplacement,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementNode,
  LexicalNode,
  NodeKey,
  SerializedElementNode,
  Spread,
} from 'lexical'
import { $generateNodesFromDOM } from '@lexical/html'
import { createHeadlessEditor } from '@lexical/headless'
import { getEditorNodes } from '../utils'
import EditorTheme from '../editor-theme'
import { CustomParagraphNode } from '../review/CustomParagraphNode'

export interface SuggestionData {
  id: string
  originalHtml: string
  suggestedHtml: string // Required HTML content for rich text suggestions
  originalBlockId?: string // Store the original blockId to restore when rejecting
}

export type SerializedSuggestionNode = Spread<
  {
    suggestionData: SuggestionData
  },
  SerializedElementNode
>

export class SuggestionNode extends ElementNode {
  __suggestionData: SuggestionData

  static getType(): string {
    return 'suggestion'
  }

  static clone(node: SuggestionNode): SuggestionNode {
    return new SuggestionNode(node.__suggestionData, node.__key)
  }

  constructor(suggestionData: SuggestionData, key?: NodeKey) {
    super(key)
    this.__suggestionData = suggestionData
  }

  getSuggestionData(): SuggestionData {
    return this.__suggestionData
  }

  setSuggestionData(suggestionData: SuggestionData): void {
    const writableNode = this.getWritable()
    writableNode.__suggestionData = suggestionData
  }

  createDOM(_: EditorConfig): HTMLElement {
    const element = document.createElement('div')
    element.classList.add('suggestion-container')
    element.setAttribute('data-suggestion-id', this.__suggestionData.id)

    if (this.__suggestionData.originalBlockId) {
      element.setAttribute('data-block-id', this.__suggestionData.originalBlockId)
    }

    const originalElement = document.createElement('div')
    originalElement.classList.add('suggestion-original')
    originalElement.innerHTML = applyEditorThemeClasses(this.__suggestionData.originalHtml)
    element.appendChild(originalElement)

    const suggestedElement = document.createElement('div')
    suggestedElement.classList.add('suggestion-new')
    suggestedElement.innerHTML = applyEditorThemeClasses(this.__suggestionData.suggestedHtml)
    element.appendChild(suggestedElement)

    return element
  }

  updateDOM(prevNode: SuggestionNode, dom: HTMLElement): boolean {
    const prevData = prevNode.getSuggestionData()
    const currentData = this.getSuggestionData()

    if (prevData.originalBlockId !== currentData.originalBlockId) {
      if (currentData.originalBlockId) {
        dom.setAttribute('data-block-id', currentData.originalBlockId)
      } else {
        dom.removeAttribute('data-block-id')
      }
    }

    // Check if the suggested HTML content has changed
    if (prevData.suggestedHtml !== currentData.suggestedHtml) {
      // Update the DOM content
      const suggestedElement = dom.querySelector('.suggestion-new')
      if (suggestedElement) {
        suggestedElement.innerHTML = applyEditorThemeClasses(currentData.suggestedHtml)
      }
      return false // We handled the update, no need for Lexical to recreate the DOM
    }

    return false // No changes needed
  }

  static transform(): (node: LexicalNode) => void {
    return (node: LexicalNode) => {
      if (!$isSuggestionNode(node)) {
        return
      }
    }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-suggestion-id')) {
          return null
        }
        return {
          conversion: convertSuggestionElement,
          priority: 1,
        }
      },
    }
  }

  static importJSON(serializedNode: SerializedSuggestionNode): SuggestionNode {
    const { suggestionData } = serializedNode
    const node = $createSuggestionNode(suggestionData)
    return node
  }

  exportDOM(): DOMExportOutput {
    const element = this.createDOM({} as EditorConfig)
    return { element }
  }

  exportJSON(): SerializedSuggestionNode {
    return {
      ...super.exportJSON(),
      suggestionData: this.__suggestionData,
      type: 'suggestion',
      version: 1,
    }
  }

  getTextContent(): string {
    const suggestedHtml = this.__suggestionData.suggestedHtml
    const dom = new DOMParser().parseFromString(suggestedHtml, 'text/html')
    const textContent = dom.body.textContent || ''

    return textContent
  }

  isInline(): boolean {
    return true
  }

  canBeEmpty(): boolean {
    return false
  }

  canInsertTextBefore(): boolean {
    return false
  }

  canInsertTextAfter(): boolean {
    return false
  }
}

function convertSuggestionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const suggestionId = domNode.getAttribute('data-suggestion-id')
  if (!suggestionId) {
    return null
  }

  const originalSpan = domNode.querySelector('.suggestion-original')
  const suggestedSpan = domNode.querySelector('.suggestion-new')

  if (!originalSpan || !suggestedSpan) {
    return null
  }

  const suggestionData: SuggestionData = {
    id: suggestionId,
    originalHtml: originalSpan.innerHTML || '',
    suggestedHtml: suggestedSpan.innerHTML || '', // Convert innerHTML back to suggestedHtml
  }

  const node = $createSuggestionNode(suggestionData)
  return { node }
}

export function $createSuggestionNode(suggestionData: SuggestionData): SuggestionNode {
  return $applyNodeReplacement(new SuggestionNode(suggestionData))
}

export function $isSuggestionNode(node: LexicalNode | null | undefined): node is SuggestionNode {
  return node instanceof SuggestionNode
}

export function $applySuggestion(suggestionNode: SuggestionNode): void {
  const suggestionData = suggestionNode.getSuggestionData()
  // Parse HTML and insert rich content
  const headlessEditor = createHeadlessEditor({
    namespace: 'ApplySuggestion',
    theme: EditorTheme,
    nodes: getEditorNodes(),
  })

  let htmlToRestore = suggestionData.suggestedHtml

  if (
    htmlToRestore &&
    !htmlToRestore.startsWith('<p') &&
    !htmlToRestore.startsWith('<h') &&
    !htmlToRestore.startsWith('<div') &&
    !htmlToRestore.startsWith('<blockquote')
  ) {
    htmlToRestore = `<p>${htmlToRestore}</p>`
  }

  const dom = new DOMParser().parseFromString(htmlToRestore, 'text/html')
  const nodes = $generateNodesFromDOM(headlessEditor, dom)

  if (nodes.length === 0) return

  // Use our custom paragraph so __blockId is preserved/rendered
  const containerNode = new CustomParagraphNode(suggestionData.originalBlockId)

  nodes.forEach(node => {
    containerNode.append(node)
  })

  suggestionNode.insertBefore(containerNode)

  suggestionNode.remove()
}

export function $rejectSuggestion(suggestionNode: SuggestionNode): void {
  const suggestionData = suggestionNode.getSuggestionData()

  // Parse HTML and insert rich content (exactly like applySuggestion)
  const headlessEditor = createHeadlessEditor({
    namespace: 'RejectSuggestion',
    theme: EditorTheme,
    nodes: getEditorNodes(),
  })

  let htmlToRestore = suggestionData.originalHtml

  if (
    htmlToRestore &&
    !htmlToRestore.startsWith('<p') &&
    !htmlToRestore.startsWith('<h') &&
    !htmlToRestore.startsWith('<div') &&
    !htmlToRestore.startsWith('<blockquote')
  ) {
    htmlToRestore = `<p>${htmlToRestore}</p>`
  }

  const dom = new DOMParser().parseFromString(htmlToRestore, 'text/html')
  const nodes = $generateNodesFromDOM(headlessEditor, dom)

  if (nodes.length === 0) return

  nodes.forEach(node => {
    // Restore the original blockId if it was stored
    if (suggestionData.originalBlockId && '__blockId' in node) {
      ;(node as any).__blockId = suggestionData.originalBlockId
    }
    suggestionNode.insertBefore(node)
  })

  suggestionNode.remove()
}

// Utility function to apply editor theme classes to HTML content
// This ensures that suggestion previews match the styling of the actual editor content
// Used in createDOM to style HTML content before it's applied to the editor
function applyEditorThemeClasses(html: string): string {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Apply theme classes to various elements
  const elements = tempDiv.querySelectorAll('*')
  elements.forEach(element => {
    const htmlElement = element as HTMLElement
    const tagName = element.tagName.toLowerCase()

    switch (tagName) {
      case 'ul':
        element.classList.add('editor-list-ul')
        break
      case 'ol':
        element.classList.add('editor-list-ol')
        break
      case 'li':
        element.classList.add('editor-listitem')
        // Check if this is a nested list item
        const parentList = element.closest('li ul, li ol')
        if (parentList) {
          element.classList.add('editor-nested-listitem')
        }
        break
      case 'p':
        element.classList.add('editor-paragraph')
        break
      case 'h1':
        element.classList.add('editor-heading-h1')
        break
      case 'h2':
        element.classList.add('editor-heading-h2')
        break
      case 'h3':
        element.classList.add('editor-heading-h3')
        break
      case 'h4':
        element.classList.add('editor-heading-h4')
        break
      case 'h5':
        element.classList.add('editor-heading-h5')
        break
      case 'h6':
        element.classList.add('editor-heading-h6')
        break
      case 'blockquote':
        element.classList.add('editor-quote')
        break
      case 'code':
        element.classList.add('editor-code')
        break
      case 'a':
        element.classList.add('editor-link')
        break
    }

    // Apply text formatting classes based on existing styles
    if (htmlElement.style.fontWeight === 'bold' || htmlElement.style.fontWeight === '700') {
      element.classList.add('editor-text-bold')
    }
    if (htmlElement.style.fontStyle === 'italic') {
      element.classList.add('editor-text-italic')
    }
    if (htmlElement.style.textDecoration?.includes('underline')) {
      element.classList.add('editor-text-underline')
    }
    if (htmlElement.style.textDecoration?.includes('line-through')) {
      element.classList.add('editor-text-strikethrough')
    }

    // Apply classes to HTML formatting elements
    if (tagName === 'b' || tagName === 'strong') {
      element.classList.add('editor-text-bold')
    }
    if (tagName === 'i' || tagName === 'em') {
      element.classList.add('editor-text-italic')
    }
    if (tagName === 'u') {
      element.classList.add('editor-text-underline')
    }
    if (tagName === 's' || tagName === 'strike') {
      element.classList.add('editor-text-strikethrough')
    }
  })

  return tempDiv.innerHTML
}
